export const TermsOfUse: React.FC = () => {
  const styles = {
    bodyBackground: {
      backgroundColor: "#f4f7f6",
      padding: "40px 20px",
      minHeight: "100vh",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      lineHeight: "1.7",
      color: "#2c3e50",
    },
    container: {
      background: "#ffffff",
      maxWidth: "850px",
      margin: "0 auto",
      padding: "50px 60px",
      borderRadius: "12px",
      boxShadow: "0 4px 15px rgba(0,0,0,0.04)",
      border: "1px solid #eef2f5",
    },
    headerBar: {
      borderLeft: "5px solid #e67e22", // Distinct warm color for Terms
      paddingLeft: "20px",
      marginBottom: "40px",
    },
    h1: {
      color: "#e67e22",
      fontSize: "2.2rem",
      margin: "0 0 10px 0",
      fontWeight: "700",
      letterSpacing: "-0.5px",
    },
    metaDate: {
      fontSize: "0.95rem",
      color: "#7f8c8d",
      margin: "0",
    },
    h2: {
      color: "#2c3e50",
      fontSize: "1.35rem",
      marginTop: "35px",
      marginBottom: "15px",
      fontWeight: "600",
      borderBottom: "1px solid #f0f4f8",
      paddingBottom: "8px",
    },
    p: {
      marginTop: "0",
      marginBottom: "1.25rem",
      color: "#4a5568",
    },
    ul: {
      marginTop: "0",
      marginBottom: "1.5rem",
      paddingLeft: "24px",
    },
    li: {
      marginBottom: "0.5rem",
      color: "#4a5568",
    },
    contactBox: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      padding: "20px",
      marginTop: "40px",
    },
    link: {
      color: "#e67e22",
      textDecoration: "none",
      fontWeight: "500",
    },
    footer: {
      marginTop: "50px",
      fontSize: "0.85rem",
      color: "#a0aec0",
      textAlign: "center",
      borderTop: "1px solid #edf2f7",
      paddingTop: "25px",
    },
  };

  return (
    <div style={styles.bodyBackground}>
      <div style={styles.container}>
        <div style={styles.headerBar}>
          <h1 style={styles.h1}>Terms of Use</h1>
          <p style={styles.metaDate}>
            <strong>Effective Date:</strong> May 20, 2026
          </p>
        </div>

        <p style={styles.p}>
          Welcome to <strong>OnlyRyan</strong> (the "App"). Please read these
          Terms of Use ("Terms") carefully before using the application. By
          downloading, accessing, or using the App, you agree to be bound by
          these Terms. If you do not agree, please do not use the App.
        </p>

        <h2 style={styles.h2}>1. Acceptance of Terms</h2>
        <p style={styles.p}>
          By accessing or using the quiz services provided by OnlyRyan, you
          agree to comply with and be bound by these Terms and our Privacy
          Policy. This agreement applies to all users, including those who
          access standard free features or subscribe to premium tiers.
        </p>

        <h2 style={styles.h2}>2. Eligibility</h2>
        <p style={styles.p}>
          To use the App and participate in its quiz formats, you must meet the
          following criteria:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Age Requirement:</strong> You must be at least 18 years old
            (or the legal age of majority in your jurisdiction).
          </li>
          <li style={styles.li}>
            <strong>Accurate Information:</strong> You must provide accurate,
            current, and complete information during account registration.
          </li>
          <li style={styles.li}>
            <strong>Legal Compliance:</strong> You must reside in a jurisdiction
            where using interactive quiz and trivial applications is legally
            permitted.
          </li>
        </ul>

        <h2 style={styles.h2}>3. User Accounts & Security</h2>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Account Security:</strong> You are entirely responsible for
            maintaining the confidentiality of your account credentials and for
            all gameplay activities that occur under your account.
          </li>
          <li style={styles.li}>
            <strong>Unauthorized Access:</strong> You agree to notify us
            immediately of any unauthorized use of your account. We are not
            liable for any loss or damage arising from your failure to protect
            your login credentials.
          </li>
          <li style={styles.li}>
            <strong>Termination:</strong> We reserve the right to suspend or
            terminate accounts that violate these Terms, disrupt the platform,
            or engage in fraudulent activities.
          </li>
        </ul>

        <h2 style={styles.h2}>4. Subscriptions & Billing</h2>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Auto-Renewing Subscriptions:</strong> The App offers premium
            features, specialized quiz sets, and advanced gameplay metrics via
            auto-renewing subscriptions.
          </li>
          <li style={styles.li}>
            <strong>Billing and Payments:</strong> All premium payments are
            charged securely directly to your designated payment method through
            the Google Play Billing API.
          </li>
          <li style={styles.li}>
            <strong>Cancellation:</strong> You can easily cancel your premium
            subscription at any time through your Google Play Store account
            subscription settings. Cancellations take effect at the end of the
            current active billing cycle.
          </li>
        </ul>

        <h2 style={styles.h2}>5. In-App Points & Gameplay Progression</h2>
        <p style={styles.p}>
          OnlyRyan features interactive leaderboards, point accumulations, and
          skill achievements. All points, ranks, scores, or digital badges used
          or earned within the application are strictly virtual game elements.
          They carry <strong>no real-world monetary value</strong>, do not
          constitute any form of financial property or digital wallet balances,
          and cannot be redeemed, transferred, or exchanged for cash or physical
          goods.
        </p>

        <h2 style={styles.h2}>6. User Conduct</h2>
        <p style={styles.p}>You agree strictly not to use the App to:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Violate any local, regional, national, or international laws or
            digital regulations.
          </li>
          <li style={styles.li}>
            Infringe upon the intellectual property rights of OnlyRyan or any
            third party.
          </li>
          <li style={styles.li}>
            Use automated bots, cheats, background scripts, emulators, or
            unauthorized modifications to manipulate game scores or leaderboard
            results.
          </li>
        </ul>

        <h2 style={styles.h2}>7. Intellectual Property</h2>
        <p style={styles.p}>
          All content included within the App—including text, graphics, quiz
          questions, interface designs, logos, and proprietary source
          software—is the sole property of OnlyRyan and is protected by
          applicable copyright and intellectual property laws. You are granted a
          limited, personal, non-transferable, and non-commercial license to use
          the App.
        </p>

        <h2 style={styles.h2}>8. Limitation of Liability</h2>
        <p style={styles.p}>
          The App is provided on an "as-is" and "as-available" basis. OnlyRyan
          will not be liable for any direct, indirect, or consequential damages
          resulting from your use of the application, including temporary server
          maintenance, unexpected service interruptions, data loss, or
          connectivity issues.
        </p>

        <h2 style={styles.h2}>9. Changes to Terms</h2>
        <p style={styles.p}>
          We reserve the right to modify these Terms of Use at any time. We will
          notify users of updates by altering the "Effective Date" at the top of
          this document. Your continued use of the App following any update
          constitutes full acceptance of the revised Terms.
        </p>

        <div style={styles.contactBox}>
          <h2 style={{ ...styles.h2, marginTop: 0, borderBottom: "none" }}>
            10. Contact Information
          </h2>
          <p style={styles.p}>
            If you have any questions, feedback, or concerns regarding these
            Terms of Use, please reach out directly to our support team:
          </p>
          <p style={{ ...styles.p, marginBottom: 0, marginTop: "10px" }}>
            <strong>Email Support:</strong>{" "}
            <a href="mailto:dev@onlyryan.com" style={styles.link}>
              dev@onlyryan.com
            </a>
          </p>
        </div>

        {/* <div style={styles.footer}>
          &copy; {new Date().getFullYear()} OnlyRyan. All Rights Reserved.
        </div> */}
      </div>
    </div>
  );
};

export default TermsOfUse;
